/**
 * The master Pre-billing page's read: GET /api/t/{slug}/v1/prebilling.
 *
 * Every client's note lines for a date range, filtered, one page at a
 * time, with the server's total and status counts, the drafts panel and
 * the pickers' options. `amount` is present on a row only for a seat
 * holding view_invoice (the server leaves the key out for any other seat).
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import type { BillingReadiness } from '@/lib/prebilling';

export interface MasterPrebillingRow {
  note_id: string;
  occurred_at: string;
  hours: string | null;
  rate_band: string | null;
  after_hours: boolean;
  invoice_summary: string | null;
  invoice_line: string | null;
  invoice_summary_skip_reason: string | null;
  status: string;
  readiness: BillingReadiness;
  author_display_name: string | null;
  author_user_id: string;
  billing_ready_at: string | null;
  billing_ready_by_display_name: string | null;
  billing_ready_by_viewer: boolean;
  client_id: string;
  client_display_name: string;
  client_is_demo: boolean;
  lead_cm_name: string | null;
  amount?: string | null;
}

export interface DraftClientSummary {
  client_id: string;
  client_display_name: string;
  client_is_demo: boolean;
  count: number;
  authors: Array<{ user_id: string; display_name: string; count: number }>;
}

export interface MasterPrebillingResponse {
  from: string;
  to: string;
  native_from: string | null;
  rows: MasterPrebillingRow[];
  total: number;
  page: number;
  page_size: number;
  counts: { all: number; ready: number; not_ready: number };
  drafts: DraftClientSummary[];
  options: {
    authors: Array<{ user_id: string; display_name: string }>;
    lead_cms: Array<{ roster_id: string; name: string }>;
    clients: Array<{ client_id: string; display_name: string; is_demo: boolean }>;
  };
}

export type MasterPrebillingStatus = 'all' | 'ready' | 'not_ready';

export interface MasterPrebillingQuery {
  from: string;
  to: string;
  status: MasterPrebillingStatus;
  /** '' = everyone. */
  author: string;
  lead_cm: string;
  client: string;
  /** null = every band; '' = no band. */
  band: string | null;
  page: number;
  page_size: number;
}

/** The longest range one read covers, inclusive; the server refuses more. */
export const MAX_RANGE_DAYS = 366;
export const PAGE_SIZE = 50;

/** Inclusive days from `from` to `to`, or null when either is not a day. */
export function rangeDays(from: string, to: string): number | null {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return (b - a) / 86_400_000 + 1;
}

/** This month's first and last day for a 'YYYY-MM-DD' today. */
export function monthBoundsOf(today: string): { from: string; to: string } {
  const [y, m] = today.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, '0');
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(last).padStart(2, '0')}` };
}

/** What the page's address says it shows: the status and the dates. */
export interface PrebillingAllView {
  from: string;
  to: string;
  status: MasterPrebillingStatus;
}

const DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * The page's status and dates from its address, so another page can link
 * to them (Invoices links to `?status=not_ready&month=YYYY-MM`):
 *   - `status` is all, ready or not_ready; anything else reads as all;
 *   - `month=YYYY-MM` is that month's first to last day; else `from` and
 *     `to`, both days, are the range; else this month (`today`'s).
 */
export function readPrebillingAllView(params: URLSearchParams, today: string): PrebillingAllView {
  const raw = params.get('status');
  const status: MasterPrebillingStatus = raw === 'ready' || raw === 'not_ready' ? raw : 'all';
  const month = params.get('month');
  if (month !== null && MONTH.test(month)) return { ...monthBoundsOf(`${month}-01`), status };
  const from = params.get('from');
  const to = params.get('to');
  if (from !== null && to !== null && DAY.test(from) && DAY.test(to)) return { from, to, status };
  return { ...monthBoundsOf(today), status };
}

/**
 * The address for a view, keeping every other parameter as it is. What
 * matches the page's opening view is left out: status all, and this
 * month. A whole month is written as `month`; any other range as `from`
 * and `to`.
 */
export function prebillingAllSearch(
  current: URLSearchParams,
  view: PrebillingAllView,
  today: string,
): URLSearchParams {
  const next = new URLSearchParams(current);
  if (view.status === 'all') next.delete('status');
  else next.set('status', view.status);
  next.delete('month');
  next.delete('from');
  next.delete('to');
  const whole = monthBoundsOf(`${view.from.slice(0, 7)}-01`);
  if (whole.from === view.from && whole.to === view.to) {
    const thisMonth = monthBoundsOf(today);
    if (whole.from !== thisMonth.from) next.set('month', view.from.slice(0, 7));
  } else {
    next.set('from', view.from);
    next.set('to', view.to);
  }
  return next;
}

export function masterPrebillingKey(slug: string, q: MasterPrebillingQuery): readonly unknown[] {
  return ['master-prebilling', slug, q] as const;
}

export async function fetchMasterPrebilling(
  slug: string,
  q: MasterPrebillingQuery,
  signal?: AbortSignal,
): Promise<MasterPrebillingResponse> {
  const params = new URLSearchParams({
    from: q.from,
    to: q.to,
    page: String(q.page),
    page_size: String(q.page_size),
  });
  if (q.status !== 'all') params.set('status', q.status);
  if (q.author !== '') params.set('author', q.author);
  if (q.lead_cm !== '') params.set('lead_cm', q.lead_cm);
  if (q.client !== '') params.set('client', q.client);
  if (q.band !== null) params.set('band', q.band);
  const path = `/api/t/${encodeURIComponent(slug)}/v1/prebilling?${params.toString()}`;
  const res = await fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `GET ${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as MasterPrebillingResponse;
}

export function useMasterPrebilling(slug: string, q: MasterPrebillingQuery, enabled: boolean) {
  return useQuery<MasterPrebillingResponse>({
    queryKey: masterPrebillingKey(slug, q),
    queryFn: ({ signal }) => fetchMasterPrebilling(slug, q, signal),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    enabled: enabled && slug !== '',
  });
}
